import {
  registerCandidateForPublicRemote,
  type CandidatePublicControllerApiRegisterCandidateForPublicRequest,
  type PublicCandidateDocumentCreateRequest,
} from '@flex-apis/recruiting';
import { dateToISODatePart } from '@flex-packages/date';
import { useFetcherFromRemote } from '@flex-packages/remote-hooks';
import { isNonNil } from '@flex-packages/utils';
import {
  candidateEducationalBackgroundSchema,
  candidateWorkExperienceBackgroundSchema,
  VeteranWhether,
  type CandidateDocumentFieldModel,
  type CandidateEducationalBackground,
  type CandidateWorkExperienceBackground,
} from '@flex-private-packages/recruiting-common/base';
import { useMutation } from '@tanstack/react-query';

import type { PublicJobDescriptionDetail } from '../../../../../base';
import type { JobDescriptionFormState } from '../models';

interface Props {
  jobDescription: PublicJobDescriptionDetail;
}

export function useSubmitJobDescriptionApplyForm({ jobDescription }: Props) {
  const submit = useFetcherFromRemote({
    remote: registerCandidateForPublicRemote,
  });

  // TODO: 강렬하게 정리하고 싶은 함수
  const handleSubmit = async (data: JobDescriptionFormState) => {
    const {
      name,
      email,
      phoneNumber,
      applicationPath,
      documents,
      hasVeteransCompensation,
      handicapCase,
      militaryServiceStatus,
      preQuestionAnswers,
      educationalBackgrounds = [],
      noEducationalBackgrounds,
      workExperienceBackgrounds = [],
      noWorkExperience,
      hasTalentPoolConsent,
      agreedToSensitiveInformationTerms,
    } = data;

    const preQuestions = jobDescription.preQuestionList.map(
      (preQuestion, index) => ({
        preQuestionIdHash: preQuestion.idHash,
        content: preQuestionAnswers?.[index],
      })
    );

    // NOTE: 민감정보(장애·보훈) 수집에 동의하지 않으면 입력값이 있어도 payload 에서 제외한다 (EPDIV-1271).
    // 동의문상 수집 항목은 '장애 사항, 보훈 대상 여부'뿐이며, 병역(military)은 민감정보가 아니라 제외 대상이 아니다.
    const consentedToSensitiveInformation =
      agreedToSensitiveInformationTerms === true;

    const payload: CandidatePublicControllerApiRegisterCandidateForPublicRequest =
      {
        jobDescriptionIdHash: jobDescription.jobDescriptionIdHash,
        candidatePublicCreateRequest: {
          applicationPath,
          email: email,
          name,
          phoneNumber,
          publicCandidateDocuments: documents
            .map(parseDocument)
            .filter(isNonNil),
          // 미응답(undefined)은 비대상(false)이 아니라 null 로 전송한다 (BE 필드 nullable).
          hasVeteransCompensation:
            consentedToSensitiveInformation && isNonNil(hasVeteransCompensation)
              ? hasVeteransCompensation === VeteranWhether.Included
              : undefined,
          handicapCaseString: consentedToSensitiveInformation
            ? handicapCase
            : undefined, // https://flex-cv82520.slack.com/archives/C02GDF2SZPS/p1658118823483029
          militaryServiceStatusString: militaryServiceStatus, // https://flex-cv82520.slack.com/archives/C02GDF2SZPS/p1658118823483029
          hasTalentPoolConsent: hasTalentPoolConsent ?? false,
          hasSensitiveInformationConsent: agreedToSensitiveInformationTerms,
          candidatePreQuestionModelsJsonString: JSON.stringify(preQuestions),
          noEducationExperience: noEducationalBackgrounds,
          educationExperiencesJsonString: JSON.stringify(
            // 학력없음 처리
            noEducationalBackgrounds
              ? []
              : formatEducationalBackgrounds(educationalBackgrounds)
          ),
          noWorkExperience,
          workExperiencesJsonString: JSON.stringify(
            // 경력없음 처리
            noWorkExperience
              ? []
              : formatWorkExperienceBackgrounds(workExperienceBackgrounds)
          ),
        },
      };

    await submit(payload);
  };

  return useMutation({ mutationFn: handleSubmit });
}

function parseDocument(
  document: CandidateDocumentFieldModel
): PublicCandidateDocumentCreateRequest | undefined {
  switch (document.registeredType) {
    case 'FILE': {
      if (document.file?.fileKey) {
        return {
          attachmentDocumentIdHash: document.requestDocumentIdHash,
          attachmentDocumentRequiredType: 'FILE',
          temporaryFileKey: document.file.fileKey,
        };
      }
      return;
    }
    case 'URL': {
      return document.url
        ? {
            attachmentDocumentIdHash: document.requestDocumentIdHash,
            attachmentDocumentRequiredType: 'URL_OR_FILE',
            url: document.url,
          }
        : undefined;
    }
  }
}

function formatEducationalBackgrounds(
  educationalBackgrounds: CandidateEducationalBackground[]
) {
  return educationalBackgrounds
    .filter(
      educationalBackground =>
        candidateEducationalBackgroundSchema.safeParse(educationalBackground)
          .success
    )
    .map(
      ({
        name,
        institutionType,
        graduationStatus,
        majorName,
        admissionDate,
        graduationDate,
      }) => {
        return {
          name,
          level: institutionType,
          graduateType: graduationStatus,
          majorName: majorName,
          beginDate: isNonNil(admissionDate)
            ? dateToISODatePart(admissionDate)
            : undefined,
          endDate: isNonNil(graduationDate)
            ? dateToISODatePart(graduationDate)
            : undefined,
        };
      }
    );
}

function formatWorkExperienceBackgrounds(
  workExperienceBackgrounds: CandidateWorkExperienceBackground[]
) {
  return workExperienceBackgrounds
    .filter(
      workExperienceBackground =>
        candidateWorkExperienceBackgroundSchema.safeParse(
          workExperienceBackground
        ).success
    )
    .map(
      ({
        name,
        contractType,
        jobRoleName,
        workExperienceStatus,
        joinDate,
        retirementDate,
      }) => {
        return {
          name,
          contractType,
          status: workExperienceStatus,
          jobRoleName,
          beginDate: isNonNil(joinDate)
            ? dateToISODatePart(joinDate)
            : undefined,
          endDate: isNonNil(retirementDate)
            ? dateToISODatePart(retirementDate)
            : undefined,
        };
      }
    );
}
